import { Link } from 'react-router-dom'
import {
  Search,
  Calendar,
  Car,
  Star,
  Shield,
  CreditCard,
  Clock,
  CheckCircle2,
  HelpCircle,
  ChevronDown,
  Award,
  Smartphone,
  MapPin,
} from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Step {
  icon: React.ReactNode
  number: number
  title: string
  description: string
  color: string
  bgColor: string
}

interface FAQItem {
  question: string
  answer: string
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const STUDENT_STEPS: Step[] = [
  {
    number: 1,
    icon: <Search className="h-7 w-7" />,
    title: 'Busque um instrutor',
    description:
      'Use os filtros para encontrar instrutores na sua cidade, com especialidades que combinam com seu objetivo e dentro do seu orçamento.',
    color: 'text-brand-600',
    bgColor: 'bg-brand-50',
  },
  {
    number: 2,
    icon: <Star className="h-7 w-7" />,
    title: 'Veja perfis e avaliações',
    description:
      'Acesse o perfil completo de cada instrutor: experiência, veículo, especialidades e avaliações reais de outros alunos.',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
  },
  {
    number: 3,
    icon: <Calendar className="h-7 w-7" />,
    title: 'Agende sua aula',
    description:
      'Escolha a data, horário e duração da aula de forma rápida. O instrutor confirmará o agendamento em até 24 horas.',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
  },
  {
    number: 4,
    icon: <CreditCard className="h-7 w-7" />,
    title: 'Pague com segurança',
    description:
      'Pagamento via Mercado Pago: cartão de crédito, Pix ou boleto. O valor só é liberado ao instrutor após a conclusão da aula.',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
  },
  {
    number: 5,
    icon: <Car className="h-7 w-7" />,
    title: 'Faça sua aula',
    description:
      'Encontre o instrutor no local combinado e aproveite sua aula. Esteja com sua permissão ou CNH provisória em mão.',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
  },
  {
    number: 6,
    icon: <Star className="h-7 w-7" />,
    title: 'Avalie o instrutor',
    description:
      'Após a aula, deixe uma avaliação honesta. Isso ajuda outros alunos e contribui para a qualidade da plataforma.',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
  },
]

const TRUST_BADGES = [
  {
    icon: <Shield className="h-8 w-8 text-brand-600" />,
    title: 'Instrutores verificados',
    description: 'Todos os instrutores têm CNH e dados verificados pela nossa equipe.',
  },
  {
    icon: <CreditCard className="h-8 w-8 text-emerald-600" />,
    title: 'Pagamento protegido',
    description: 'Seu dinheiro fica seguro até a aula ser concluída com sucesso.',
  },
  {
    icon: <Clock className="h-8 w-8 text-orange-600" />,
    title: 'Flexibilidade de horários',
    description: 'Agende aulas nos dias e horários que funcionam para você.',
  },
  {
    icon: <Smartphone className="h-8 w-8 text-violet-600" />,
    title: 'Tudo pelo app ou site',
    description: 'Gerencie agendamentos, pagamentos e comunicação em um só lugar.',
  },
  {
    icon: <MapPin className="h-8 w-8 text-red-500" />,
    title: 'Instrutores próximos',
    description: 'Encontre instrutores que atendem no seu bairro ou região.',
  },
  {
    icon: <Award className="h-8 w-8 text-amber-600" />,
    title: 'Qualidade garantida',
    description: 'Avaliações reais de alunos para escolher com confiança.',
  },
]

const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'Preciso ter permissão de aprendizagem para fazer aulas?',
    answer:
      'Sim, para aulas práticas em vias públicas você precisa da Permissão para Aprender a Dirigir (PAD), emitida pelo DETRAN do seu estado. Aulas em propriedades privadas podem ter regras diferentes.',
  },
  {
    question: 'Como funciona o pagamento?',
    answer:
      'O pagamento é processado de forma segura pelo Mercado Pago. Você pode pagar com cartão de crédito (parcelado), Pix ou boleto bancário. O valor é retido até a conclusão da aula e só então liberado ao instrutor.',
  },
  {
    question: 'E se eu precisar cancelar uma aula?',
    answer:
      'Você pode cancelar com pelo menos 24 horas de antecedência para receber reembolso integral. Cancelamentos com menos de 24 horas podem estar sujeitos a uma taxa de cancelamento conforme a política do instrutor.',
  },
  {
    question: 'O instrutor busca o aluno em casa?',
    answer:
      'Depende de cada instrutor. Muitos oferecem busca e entrega no endereço do aluno dentro do raio de atendimento. Confira as informações no perfil do instrutor antes de agendar.',
  },
  {
    question: 'Posso escolher um veículo automático?',
    answer:
      'Sim! Use o filtro de especialidades para encontrar instrutores que oferecem aulas em veículos automáticos ou semi-automáticos.',
  },
  {
    question: 'Como sei se o instrutor é confiável?',
    answer:
      'Todos os instrutores passam por uma verificação de documentos antes de serem aprovados na plataforma. Além disso, você pode ver o histórico de avaliações de outros alunos no perfil de cada instrutor.',
  },
  {
    question: 'Posso fazer aulas para tirar habilitação B, A ou outra categoria?',
    answer:
      'Sim! Nossa plataforma tem instrutores especializados em diversas categorias: A (motos), B (carros), C, D e E (veículos pesados). Use os filtros de especialidade para encontrar o instrutor certo.',
  },
  {
    question: 'E se eu tiver uma experiência ruim com um instrutor?',
    answer:
      'Nossa equipe de suporte está disponível para ajudar. Entre em contato pelo suporte@meudriver.com.br ou pelo chat no aplicativo. Avaliamos cada caso individualmente.',
  },
]

// ─── FAQ Accordion item ───────────────────────────────────────────────────────

function FAQAccordionItem({ item, isOpen, onToggle }: {
  item: FAQItem
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-4 py-5 text-left hover:text-brand-600 transition-colors group"
      >
        <span className="font-medium text-gray-900 group-hover:text-brand-600 transition-colors">
          {item.question}
        </span>
        <ChevronDown
          className={`h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="pb-5 pr-9">
          <p className="text-gray-600 leading-relaxed">{item.answer}</p>
        </div>
      )}
    </div>
  )
}

// ─── Step card ────────────────────────────────────────────────────────────────

function StepCard({ step }: { step: Step }) {
  return (
    <div className="relative flex gap-5">
      {/* Connector line */}
      <div className="flex flex-col items-center">
        <div className={`rounded-2xl p-3.5 ${step.bgColor} ${step.color} flex-shrink-0 z-10`}>
          {step.icon}
        </div>
        {step.number < 6 && (
          <div className="w-px flex-1 bg-gray-200 my-2" />
        )}
      </div>
      <div className="pb-8">
        <div className="flex items-center gap-2 mb-1">
          <Badge variant="secondary" className="text-xs font-mono">
            Passo {step.number}
          </Badge>
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
        <p className="mt-1.5 text-gray-500 leading-relaxed">{step.description}</p>
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function HowItWorksPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null)

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <Badge className="mb-4 bg-white/20 text-white border-0 text-sm">
            Como funciona
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-5">
            Aprender a dirigir nunca foi tão fácil
          </h1>
          <p className="text-xl text-brand-100 leading-relaxed max-w-2xl mx-auto">
            No MeuDriver você encontra o instrutor ideal, agenda no seu horário
            e paga com segurança — tudo em poucos minutos.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button
              asChild
              size="lg"
              className="bg-white text-brand-700 hover:bg-brand-50 font-semibold px-8"
            >
              <Link to="/instrutores">Encontrar instrutor</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/40 text-white hover:bg-white/10 font-semibold px-8"
            >
              <Link to="/cadastro">Criar conta grátis</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold text-gray-900">
            Do cadastro à aula em 6 passos
          </h2>
          <p className="mt-3 text-gray-500 text-lg">
            Um processo simples, transparente e seguro para você.
          </p>
        </div>
        <div className="max-w-lg mx-auto">
          {STUDENT_STEPS.map((step) => (
            <StepCard key={step.number} step={step} />
          ))}
        </div>
      </section>

      {/* Trust badges */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Por que escolher o MeuDriver?
            </h2>
            <p className="mt-3 text-gray-500 text-lg">
              Segurança, conveniência e qualidade em cada aula.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TRUST_BADGES.map((badge) => (
              <div
                key={badge.title}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-3"
              >
                <div className="rounded-xl bg-gray-50 w-fit p-3">{badge.icon}</div>
                <h3 className="font-semibold text-gray-900">{badge.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {badge.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-brand-600 font-medium mb-3">
            <HelpCircle className="h-5 w-5" />
            Dúvidas frequentes
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            Perguntas frequentes
          </h2>
          <p className="mt-3 text-gray-500">
            Não encontrou o que procura?{' '}
            <a
              href="mailto:suporte@meudriver.com.br"
              className="text-brand-600 hover:underline"
            >
              Entre em contato
            </a>
            .
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-6">
          {FAQ_ITEMS.map((item, index) => (
            <FAQAccordionItem
              key={index}
              item={item}
              isOpen={openFAQ === index}
              onToggle={() => setOpenFAQ(openFAQ === index ? null : index)}
            />
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-brand-600 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Pronto para começar?
          </h2>
          <p className="text-brand-100 text-lg mb-8">
            Crie sua conta grátis e encontre seu instrutor hoje mesmo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-white text-brand-700 hover:bg-brand-50 font-semibold px-10"
            >
              <Link to="/cadastro">Criar conta grátis</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/40 text-white hover:bg-white/10 font-semibold px-10"
            >
              <Link to="/instrutores">Ver instrutores</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}
